import React, { useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Input,
  Modal,
  ModalClose,
  ModalDialog,
  Sheet,
  Table,
  Typography,
} from '@mui/joy';
import type { DooColour, StatusFilter } from '../types';
import {
  colourDependents,
  createColour,
  deleteColour,
  formatDependents,
  listColours,
  updateColour,
} from '../data/repository';
import { catalogueStatus, matchesStatusFilter } from '../data/status';
import { useAsyncList } from './NamedEntitySection';
import {
  ConfirmDialog,
  DivestFields,
  StatusChip,
  StatusFilterSelect,
  Toolbar,
} from './FormControls';

export function ColoursSection() {
  const { rows, loading, error, reload } = useAsyncList(listColours);
  const [filter, setFilter] = useState<StatusFilter>('active');
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState<DooColour | null>(null);
  const [name, setName] = useState('');
  const [hex, setHex] = useState('#ffffff');
  const [ral, setRal] = useState('');
  const [divest, setDivest] = useState(false);
  const [deprecatedDate, setDeprecatedDate] = useState('');
  const [formError, setFormError] = useState<string | null>(null);
  const [deleteRow, setDeleteRow] = useState<DooColour | null>(null);

  const visible = useMemo(
    () => rows.filter((row) => matchesStatusFilter(row, filter)),
    [rows, filter]
  );

  const openCreate = () => {
    setEditing(null);
    setName('');
    setHex('#ffffff');
    setRal('');
    setDivest(false);
    setDeprecatedDate('');
    setFormError(null);
    setOpen(true);
  };

  const openEdit = (row: DooColour) => {
    setEditing(row);
    setName(row.colour_name);
    setHex(row.hex_value || '#ffffff');
    setRal(row.ral_code || '');
    setDivest(Boolean(row.divest));
    setDeprecatedDate(row.deprecated_date?.slice(0, 10) || '');
    setFormError(null);
    setOpen(true);
  };

  const payload = () => ({
    colour_name: name.trim(),
    hex_value: hex.trim() || null,
    ral_code: ral.trim() || null,
    divest,
    deprecated_date: deprecatedDate || null,
  });

  return (
    <Box>
      <Typography level="body-sm" sx={{ color: 'neutral.500', mb: 2 }}>
        Door slab paint and RAL finishes. Hardware metal finishes are managed separately.
      </Typography>
      {error ? <Alert color="danger" sx={{ mb: 2 }}>{error}</Alert> : null}
      {formError && !open ? <Alert color="warning" sx={{ mb: 2 }}>{formError}</Alert> : null}
      <Toolbar>
        <StatusFilterSelect value={filter} onChange={setFilter} />
        <Button onClick={openCreate}>Add colour</Button>
      </Toolbar>
      <Sheet variant="outlined" sx={{ overflow: 'auto' }}>
        <Table stickyHeader>
          <thead>
            <tr>
              <th>Colour Name</th>
              <th>HEX</th>
              <th>RAL</th>
              <th>Status</th>
              <th>Deprecated Date</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={6}>Loading…</td></tr>
            ) : (
              visible.map((row) => (
                <tr key={row.colour_id}>
                  <td>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Box
                        sx={{
                          width: 18,
                          height: 18,
                          borderRadius: '50%',
                          border: '1px solid',
                          borderColor: 'divider',
                          bgcolor: row.hex_value || '#fff',
                        }}
                      />
                      {row.colour_name}
                    </Box>
                  </td>
                  <td>{row.hex_value}</td>
                  <td>{row.ral_code}</td>
                  <td><StatusChip status={catalogueStatus(row)} /></td>
                  <td>{row.deprecated_date?.slice(0, 10) || '—'}</td>
                  <td>
                    <Button size="sm" variant="plain" onClick={() => openEdit(row)}>Edit</Button>
                    <Button
                      size="sm"
                      variant="plain"
                      color="danger"
                      onClick={async () => {
                        const dependents = await colourDependents(row.colour_id);
                        if (dependents.length) {
                          setFormError(
                            `Cannot delete “${row.colour_name}” while it is used by ${formatDependents(dependents)}. Divest or deprecate it instead.`
                          );
                          return;
                        }
                        setDeleteRow(row);
                      }}
                    >
                      Delete
                    </Button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </Table>
      </Sheet>

      <Modal open={open} onClose={() => setOpen(false)}>
        <ModalDialog sx={{ minWidth: 460 }}>
          <ModalClose />
          <Typography level="title-lg">{editing ? 'Edit colour' : 'New colour'}</Typography>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, mt: 1 }}>
            {formError ? <Alert color="danger">{formError}</Alert> : null}
            <Input placeholder="Colour name" value={name} onChange={(e) => setName(e.target.value)} />
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Input type="color" value={hex} onChange={(e) => setHex(e.target.value)} sx={{ width: 72 }} />
              <Input placeholder="HEX" value={hex} onChange={(e) => setHex(e.target.value)} />
            </Box>
            <Input placeholder="RAL" value={ral} onChange={(e) => setRal(e.target.value)} />
            <DivestFields
              divest={divest}
              deprecatedDate={deprecatedDate}
              onDivest={setDivest}
              onDeprecatedDate={setDeprecatedDate}
            />
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
              <Button variant="outlined" onClick={() => setOpen(false)}>Cancel</Button>
              <Button
                onClick={async () => {
                  if (!name.trim()) {
                    setFormError('Colour name is required.');
                    return;
                  }
                  try {
                    if (editing) await updateColour(editing.colour_id, payload());
                    else await createColour(payload());
                    setOpen(false);
                    reload();
                  } catch (err) {
                    setFormError(err instanceof Error ? err.message : 'Save failed');
                  }
                }}
              >
                Save
              </Button>
            </Box>
          </Box>
        </ModalDialog>
      </Modal>

      <ConfirmDialog
        open={Boolean(deleteRow)}
        title="Delete colour?"
        message={deleteRow ? `Delete “${deleteRow.colour_name}”? Prefer deprecation for established colours.` : ''}
        onClose={() => setDeleteRow(null)}
        onConfirm={async () => {
          if (!deleteRow) return;
          try {
            await deleteColour(deleteRow.colour_id);
            setDeleteRow(null);
            reload();
          } catch (err) {
            setDeleteRow(null);
            setFormError(err instanceof Error ? err.message : 'Delete failed');
          }
        }}
      />
    </Box>
  );
}
